import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';
import { toast } from '@hivebrite/design-system/Toast';

import NotFound from 'hb-react/frontoffice/apps/Mentoring/components/NotFound';
import { CreateMenteeToMentorForm } from 'hb-react/frontoffice/apps/Mentoring/container/Application/MenteeToMentor/CreateMenteeToMentorForm';
import { ReapplyMenteeToMentorForm } from 'hb-react/frontoffice/apps/Mentoring/container/Application/MenteeToMentor/ReapplyMenteeToMentorForm';
import { useGetMentorProfile } from 'hb-react/frontoffice/apps/Mentoring/hooks/useGetMentorProfile';
import { type Program } from 'hb-react/frontoffice/apps/Mentoring/types';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

interface ApplyMenteeToMentorProps {
  program: Program;
}

export const ApplyMenteeToMentor = ({ program }: ApplyMenteeToMentorProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentUser } = useDefaultAppContexts();
  const { mentorId = '' } = useParams<{
    mentorId: string;
  }>();
  const { data: mentor, isLoading: isLoadingMentor } = useGetMentorProfile({
    program,
    mentorId: Number(mentorId),
  });

  if (isLoadingMentor) {
    return <Loader size={40} />;
  }

  if (!mentor) {
    return <NotFound />;
  }

  const {
    user_id: userId,
    status,
    name,
    relationship_with_current_user: relationshipWithCurrentUser,
  } = mentor;

  if (currentUser?.id === userId) {
    return <Navigate to="/" />;
  }

  if (status !== 'accepted') {
    return <NotFound />;
  }

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [ITEMS_QUERY_KEYS.mentoring.items(program.id)],
    });
    toast.success(s__('Mentoring|Your request has been submitted'));
    navigate('../../../mentors');
  };

  if (
    relationshipWithCurrentUser?.status === 'terminated' ||
    relationshipWithCurrentUser?.status === 'rejected'
  ) {
    return (
      <ReapplyMenteeToMentorForm
        program={program}
        mentorName={name}
        relationshipWithCurrentUser={relationshipWithCurrentUser}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <CreateMenteeToMentorForm
      program={program}
      mentor={mentor}
      onSuccess={onSuccess}
    />
  );
};
